/**
 * RESOURCES PAGE — /resources
 * Free tools, credits, and resources for AI founders.
 * Permanently features emma + other credit programs.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ExternalLink, ArrowRight, CheckCircle, Zap, Star } from 'lucide-react';

const CLOUD_CREDITS = [
  {
    name: 'emma',
    tagline: 'Up to $300K in GPU Credits',
    description: 'Multi-cloud GPU access (H100, H200, B300) with no waitlist. Private network backbone cuts egress costs ~70%. The best infrastructure credits program available right now.',
    url: 'https://credits.emma.ms',
    badge: 'TOP PICK',
    badgeColor: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    highlight: true,
    perks: ['H100 / H200 / B300 — available now', 'Multi-cloud: AWS, Azure, GCP + bare-metal', '~70% egress cost savings', 'White-glove migration support', 'Response in under 5 hours'],
    amount: '$300K',
    amountColor: 'text-amber-400',
  },
  {
    name: 'Google for Startups',
    tagline: 'Up to $200K in Google Cloud Credits',
    description: 'Two-year program with up to $200K in year one. Best for Vertex AI, Gemini API, and BigQuery.',
    url: 'https://cloud.google.com/startup',
    badge: '$200K',
    badgeColor: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    highlight: false,
    perks: ['Vertex AI & Gemini API access', 'BigQuery for data infrastructure', 'Google engineer technical support'],
    amount: '$200K',
    amountColor: 'text-blue-400',
  },
  {
    name: 'Cloudflare for Startups',
    tagline: 'Up to $250K in Cloudflare Credits',
    description: 'Zero-egress R2 storage, Workers edge compute, and AI Gateway for LLM cost optimization. Massively underrated.',
    url: 'https://www.cloudflare.com/forstartups',
    badge: '$250K',
    badgeColor: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    highlight: false,
    perks: ['R2 storage — zero egress fees', 'AI Gateway for LLM caching', 'Workers serverless compute'],
    amount: '$250K',
    amountColor: 'text-orange-400',
  },
  {
    name: 'Microsoft Founders Hub',
    tagline: 'Up to $150K in Azure Credits',
    description: 'Azure OpenAI access, GitHub Enterprise, and Microsoft 365. Best for teams building on Azure OpenAI.',
    url: 'https://foundershub.startups.microsoft.com',
    badge: '$150K',
    badgeColor: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    highlight: false,
    perks: ['Azure OpenAI Service access', 'GitHub Enterprise included', 'Microsoft 365 Business Premium'],
    amount: '$150K',
    amountColor: 'text-cyan-400',
  },
  {
    name: 'AWS Activate',
    tagline: 'Up to $100K in AWS Credits',
    description: 'The most well-known program. Good for general AWS infrastructure. Apply for Portfolio tier through any accelerator connection.',
    url: 'https://aws.amazon.com/activate',
    badge: '$100K',
    badgeColor: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    highlight: false,
    perks: ['AWS service credits', 'Business Support (1 year)', 'Training credits included'],
    amount: '$100K',
    amountColor: 'text-yellow-400',
  },
  {
    name: 'NVIDIA Inception',
    tagline: 'GPU Discounts + Technical Access',
    description: 'Less about cash, more about access. Preferred GPU pricing, NVIDIA engineer support, and early hardware access. Free to join.',
    url: 'https://www.nvidia.com/en-us/startups',
    badge: 'FREE',
    badgeColor: 'bg-green-500/20 text-green-400 border-green-500/30',
    highlight: false,
    perks: ['Preferred DGX Cloud pricing', 'Early GPU hardware access', 'NVIDIA VC network connections'],
    amount: 'Free',
    amountColor: 'text-green-400',
  },
];

const ResourceCard = ({ resource }) => (
  <div className={`relative bg-gray-900/70 border rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl ${resource.highlight ? 'border-amber-500/40 shadow-lg shadow-amber-500/10' : 'border-gray-700/50 hover:border-purple-500/30 hover:shadow-purple-500/10'}`}>
    {resource.highlight && (
      <div className="h-0.5 bg-gradient-to-r from-amber-400 via-orange-500 to-red-500" />
    )}
    <div className="p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-base font-black text-white">{resource.name}</h3>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${resource.badgeColor}`}>
              {resource.badge}
            </span>
          </div>
          <p className="text-xs text-gray-500">{resource.tagline}</p>
        </div>
        <span className={`text-2xl font-black ${resource.amountColor}`}>{resource.amount}</span>
      </div>

      <p className="text-xs text-gray-400 leading-relaxed mb-4">{resource.description}</p>

      <ul className="space-y-1.5 mb-5">
        {resource.perks.map((perk, i) => (
          <li key={i} className="flex items-start gap-2 text-xs text-gray-400">
            <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0 mt-0.5" />
            {perk}
          </li>
        ))}
      </ul>

      <a
        href={resource.url}
        target="_blank"
        rel="noopener noreferrer"
        className={`flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-xs font-semibold transition-all ${resource.highlight ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-black hover:from-amber-400 hover:to-orange-500 shadow-lg shadow-amber-500/20' : 'bg-gray-800/60 border border-gray-700/50 text-white hover:border-purple-500/30'}`}
        data-testid={`resource-link-${resource.name.toLowerCase().replace(/\s+/g, '-')}`}
      >
        Apply Now <ExternalLink className="w-3 h-3" />
      </a>
    </div>
  </div>
);

const ResourcesPage = () => {
  const totalCredits = '$1M+';

  return (
    <div className="min-h-screen bg-[#030305] text-white">
      {/* Ambient */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-amber-600/4 rounded-full blur-[220px]" />
        <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-purple-600/4 rounded-full blur-[220px]" />
        <div className="absolute inset-0 opacity-[0.025]" style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.2) 1px, transparent 1px)', backgroundSize: '36px 36px' }} />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-10">

        {/* Nav */}
        <div className="flex items-center justify-between mb-12">
          <Link to="/" className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-400" />
            <span className="font-bold text-white">iSupplyAI</span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-sm font-semibold hover:opacity-90 transition-opacity"
          >
            Try iSupplyAI Free <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {/* Hero */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-medium mb-5">
            <Star className="w-3 h-3" /> Curated by iSupplyAI
          </div>
          <h1 className="text-4xl sm:text-5xl font-black mb-4">
            Free Resources for
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-400">AI Founders</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-xl mx-auto mb-6">
            The programs, credits, and tools we actually use to build iSupplyAI. Updated regularly.
          </p>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 text-sm font-semibold">
            <Zap className="w-4 h-4" /> {totalCredits} in available credits below
          </div>
        </div>

        {/* Cloud Credits Section */}
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <h2 className="text-xl font-black text-white">Cloud Credits</h2>
            <div className="flex-1 h-px bg-gray-800" />
            <span className="text-xs text-gray-600">Stack them — they don't conflict</span>
          </div>

          {/* emma featured */}
          <div className="mb-5">
            <ResourceCard resource={CLOUD_CREDITS[0]} />
          </div>

          {/* Rest in grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CLOUD_CREDITS.slice(1).map((r) => (
              <ResourceCard key={r.name} resource={r} />
            ))}
          </div>
        </div>

        {/* Stack CTA */}
        <div className="rounded-2xl border border-purple-500/20 bg-gradient-to-r from-purple-900/20 via-gray-900/50 to-amber-900/10 p-10 text-center mb-16">
          <h3 className="text-2xl font-black text-white mb-3">The Smart Move: Stack All of Them</h3>
          <p className="text-gray-400 text-sm mb-3 max-w-lg mx-auto">
            These programs don't conflict. An AI startup that applies to all six could have over $1M in credits to work with — enough to scale without infrastructure costs for 12–18 months.
          </p>
          <p className="text-xs text-gray-600 mb-6">Start with emma (fastest response, biggest GPU credits), then work down the list.</p>
          <a
            href="https://credits.emma.ms"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 text-black font-bold text-sm hover:from-amber-400 hover:to-orange-500 transition-all shadow-lg shadow-amber-500/20"
            data-testid="resources-emma-cta"
          >
            Start With emma — Apply Free <ExternalLink className="w-4 h-4" />
          </a>
        </div>

        {/* iSupplyAI own plug */}
        <div className="rounded-2xl border border-purple-500/20 bg-gray-900/60 p-8 text-center">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <h3 className="text-lg font-black text-white mb-2">Also — iSupplyAI is free to start</h3>
          <p className="text-gray-400 text-sm mb-5 max-w-sm mx-auto">
            12 AI gods with distinct personalities debate your strategy in real time. No generic advice. Real arguments. Free to try.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-sm font-semibold hover:opacity-90 transition-opacity"
            data-testid="resources-isupplyai-cta"
          >
            Try iSupplyAI Free <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Press & Mentions */}
        <div className="rounded-2xl border border-gray-800/50 bg-gray-900/40 p-8" data-testid="press-mentions-section">
          <h3 className="text-lg font-black text-white mb-1 flex items-center gap-2">
            <Star className="w-5 h-5 text-amber-400" /> Press &amp; Mentions
          </h3>
          <p className="text-gray-500 text-sm mb-5">iSupplyAI has been featured on these platforms.</p>
          <div className="grid gap-3">
            <a href="https://doc.clickup.com/90182059546/d/h/2kzma1gu-2098/a88d38f67c452f8" target="_blank" rel="noopener noreferrer"
               className="flex items-center justify-between px-5 py-3.5 rounded-xl border border-gray-800/60 bg-gray-900/60 hover:border-purple-500/30 hover:bg-purple-500/5 transition-all group"
               data-testid="press-clickup">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center"><Zap className="w-4 h-4 text-purple-400" /></div>
                <div>
                  <p className="text-sm font-semibold text-gray-200">ClickUp</p>
                  <p className="text-xs text-gray-500">Featured resource document</p>
                </div>
              </div>
              <ExternalLink className="w-4 h-4 text-gray-600 group-hover:text-purple-400 transition-colors" />
            </a>
            <a href="https://www.cake.me/me/isupply-ai" target="_blank" rel="noopener noreferrer"
               className="flex items-center justify-between px-5 py-3.5 rounded-xl border border-gray-800/60 bg-gray-900/60 hover:border-blue-500/30 hover:bg-blue-500/5 transition-all group"
               data-testid="press-cake">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center"><Zap className="w-4 h-4 text-blue-400" /></div>
                <div>
                  <p className="text-sm font-semibold text-gray-200">Cake.me</p>
                  <p className="text-xs text-gray-500">Official company profile</p>
                </div>
              </div>
              <ExternalLink className="w-4 h-4 text-gray-600 group-hover:text-blue-400 transition-colors" />
            </a>
            <a href="https://the-article-network.blogspot.com/2026/01/ai-driven-supply-chain-optimization.html" target="_blank" rel="noopener noreferrer"
               className="flex items-center justify-between px-5 py-3.5 rounded-xl border border-gray-800/60 bg-gray-900/60 hover:border-emerald-500/30 hover:bg-emerald-500/5 transition-all group"
               data-testid="press-article-network">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center"><Zap className="w-4 h-4 text-emerald-400" /></div>
                <div>
                  <p className="text-sm font-semibold text-gray-200">The Article Network</p>
                  <p className="text-xs text-gray-500">AI-Driven Supply Chain Optimization</p>
                </div>
              </div>
              <ExternalLink className="w-4 h-4 text-gray-600 group-hover:text-emerald-400 transition-colors" />
            </a>
          </div>
        </div>

        <div className="text-center mt-10 text-[10px] text-gray-700">
          iSupplyAI — 12 AI Gods. One Strategy. &nbsp;·&nbsp;
          <Link to="/blog" className="hover:text-gray-500">Blog</Link> &nbsp;·&nbsp;
          <Link to="/replays" className="hover:text-gray-500">War Room Replays</Link> &nbsp;·&nbsp;
          <Link to="/tools/living-war-room" className="hover:text-gray-500">Living War Room</Link>
        </div>
      </div>
    </div>
  );
};

export default ResourcesPage;
